import { Form, InputLabel, Space, Text } from '@procurify/ui'

import { PriceFormField } from '@webapp/components/PriceField'
import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { Price } from '@webapp/core/components/Price'
import {
  type Currency,
  type CurrencyName,
} from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import { type RequestMileageConfiguration } from '@webapp/modules/expense/entities/RequestMileageConfiguration'
import { TaxFields } from './TaxFields'
import messages from '../messages'

interface MileageFieldsProps {
  currencyName: CurrencyName
  selectedCurrency: Currency | null
  onCurrencyChange: (currency: Currency | null) => void
  unitOptions: { label: string; value: string }[]
  computedSubtotal: number
  computedTotal: number
  isSubmitting: boolean
  mileageConfiguration: RequestMileageConfiguration | null
}

export const MileageFields = ({
  currencyName,
  selectedCurrency,
  onCurrencyChange,
  unitOptions,
  computedSubtotal,
  computedTotal,
  isSubmitting,
  mileageConfiguration,
}: MileageFieldsProps) => {
  const intl = useProcurifyIntl()
  const isMileageLocked = mileageConfiguration?.isLocked ?? false

  return (
    <Space>
      <Space align='center' justify='end' direction='horizontal'>
        <Space.Item>
          <InputLabel htmlFor='mileageRate' required>
            {intl.formatMessage(messages.mileageRateLabel)}
          </InputLabel>
        </Space.Item>
        <Space.Item width={1 / 3}>
          <PriceFormField
            id='mileageRate'
            name='mileageRate'
            disabled={isSubmitting || isMileageLocked}
            inputProps={{ style: { textAlign: 'right' } }}
          />
        </Space.Item>
        <Space.Item width={1 / 3}>
          <CurrencySelect
            inputId='mileageFields-currency-select'
            value={selectedCurrency}
            onChange={onCurrencyChange}
            isDisabled={isSubmitting || isMileageLocked}
            menuPortalTarget={document.body}
          />
        </Space.Item>
      </Space>

      <Space align='center' justify='end' direction='horizontal'>
        <Space.Item>
          <InputLabel htmlFor='distance' required>
            {intl.formatMessage(messages.distanceLabel)}
          </InputLabel>
        </Space.Item>
        <Space.Item width={1 / 3}>
          <Form.NumberField
            id='distance'
            name='distance'
            disabled={isSubmitting}
            inputProps={{ style: { textAlign: 'right' } }}
          />
        </Space.Item>
        <Space.Item width={1 / 3}>
          <Form.Select
            id='unitOfDistanceStr'
            name='unitOfDistanceStr'
            options={unitOptions}
            disabled={isSubmitting}
          />
        </Space.Item>
      </Space>

      <Space align='center' justify='end' direction='horizontal'>
        <Space.Item>
          <Text bold>{intl.formatMessage(messages.subtotalLabel)}</Text>
        </Space.Item>
        <Space.Item>
          <Price price={computedSubtotal} currency={currencyName} />
        </Space.Item>
      </Space>

      <TaxFields isSubmitting={isSubmitting} />

      <Space align='center' justify='end' direction='horizontal'>
        <Space.Item>
          <Text bold>{intl.formatMessage(messages.totalCostLabel)}</Text>
        </Space.Item>
        <Space.Item>
          <Price price={computedTotal} currency={currencyName} />
        </Space.Item>
      </Space>
    </Space>
  )
}
